import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DateRangeControl, InfoTip, MetricKey, defaultRange, rangePreset } from '@/components/shared';
import { ErrorBanner, Spinner } from '../Admin/shared';
import {
  Cohort,
  Metric,
  Scorecard,
  useFounderScorecard,
} from './api/useFounderScorecard';

/** `—` for a number we could not compute. Never 0 — that is a different claim. */
const fmt = (m: Metric | null | undefined): string => {
  if (!m || m.value === null) return '—';
  return m.unit === 'percent' ? `${m.value.toFixed(1)}%` : m.value.toLocaleString();
};

const fmtPct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);

/**
 * Movement against the prior period, as a tinted chip.
 *
 * Renders nothing at all on an all-time view: there is no period before all
 * time, and an empty comparison drawn as "0" would read as growth from nothing.
 */
function Delta({ m }: { m: Metric | null | undefined }) {
  if (!m || m.value === null || m.delta_pct === null) return null;
  const up = m.delta_pct >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${
        up ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
      }`}
    >
      {up ? '▲' : '▼'}
      {Math.abs(m.delta_pct).toFixed(1)}%
    </span>
  );
}

/** What the delta is measured against, spelled out rather than implied. */
function Baseline({ m, allTime }: { m: Metric | null | undefined; allTime: boolean }) {
  if (allTime) return <span className="text-[11px] text-gray-600">no earlier period</span>;
  if (!m || m.prior === null) return null;
  return (
    <span className="text-[11px] text-gray-600 tabular-nums">
      from {m.prior.toLocaleString()}
    </span>
  );
}

/**
 * The north star drawn as an area, with the number sitting on top of it.
 *
 * This is the one place the page spends any visual drama. A twelve-point series
 * is a shape you read in half a second, so it is drawn directly rather than
 * through a chart library, and it sits behind the number instead of beside it —
 * the trend is context for the figure, not a second thing to look at.
 */
function NorthStarChart({ series }: { series: { value: number | null }[] }) {
  const values = series.map((p) => p.value ?? 0);
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const w = 600;
  const h = 120;
  const pt = (v: number, i: number) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - (v / max) * (h - 8) - 4;
    return [x, y] as const;
  };
  const line = values.map((v, i) => pt(v, i).join(',')).join(' ');
  const area = `0,${h} ${line} ${w},${h}`;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="absolute inset-x-0 bottom-0 w-full h-32 pointer-events-none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="northstar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#northstar)" />
      <polyline
        points={line}
        fill="none"
        stroke="#a78bfa"
        strokeWidth={2}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/**
 * One metric inside a group strip.
 *
 * Deliberately not a card. The seven headlines are not peers — they answer
 * three separate questions — so they sit as columns inside a grouped band with
 * hairline dividers. Twelve identical rounded boxes would flatten that away.
 */
function Stat({
  label,
  metric,
  hint,
  allTime,
  info,
}: {
  label: string;
  metric: Metric | null | undefined;
  hint?: string;
  allTime: boolean;
  info?: MetricKey;
}) {
  const fraction =
    metric && metric.denominator !== undefined
      ? `${(metric.numerator ?? 0).toLocaleString()} of ${metric.denominator.toLocaleString()}`
      : null;
  return (
    <div className="flex-1 min-w-[150px] px-5 py-4">
      <p className="text-[11px] text-gray-500 font-medium">
        {label}
        {info && <InfoTip metric={info} />}
      </p>
      <p className="mt-1 text-2xl font-semibold text-white tabular-nums tracking-tight">
        {fmt(metric)}
      </p>
      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
        <Delta m={metric} />
        <Baseline m={metric} allTime={allTime} />
      </div>
      {/* The denominator matters at this volume: 33% of 3 is not 33% of 300. */}
      {fraction && <p className="mt-1 text-[11px] text-gray-600 tabular-nums">{fraction}</p>}
      {hint && <p className="mt-1 text-[11px] text-gray-600">{hint}</p>}
    </div>
  );
}

/** A band of related metrics, labelled by the question they answer. */
function Group({
  question,
  caption,
  children,
}: {
  question: string;
  caption: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-white/5 bg-[#14141f] overflow-hidden">
      <div className="flex flex-col lg:flex-row">
        <div className="lg:w-52 shrink-0 px-5 py-4 border-b lg:border-b-0 lg:border-r border-white/5 bg-white/[0.02]">
          <h2 className="text-sm font-semibold text-gray-200">{question}</h2>
          <p className="mt-1 text-[11px] text-gray-500 leading-relaxed">{caption}</p>
        </div>
        <div className="flex-1 flex flex-wrap divide-x divide-white/5">{children}</div>
      </div>
    </section>
  );
}

/** A metric group that failed server-side. Named, so it is not read as zero. */
function Unavailable({ label }: { label: string }) {
  return (
    <div className="flex-1 min-w-[150px] px-5 py-4">
      <p className="text-[11px] text-gray-500 font-medium">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-700 tabular-nums">—</p>
      <p className="mt-1.5 text-[11px] text-amber-500/80">Could not be computed</p>
    </div>
  );
}

/**
 * The retention curve.
 *
 * Tinted by value, because the question is whether the tail FLATTENS rather
 * than falls to zero, and a column of tinted cells answers that at a glance in
 * a way bare numerals do not.
 */
function heat(v: number | null): string {
  if (v === null) return 'text-gray-700';
  if (v >= 20) return 'text-emerald-300 bg-emerald-500/10';
  if (v >= 10) return 'text-emerald-400/80 bg-emerald-500/5';
  if (v > 0) return 'text-amber-400/80 bg-amber-500/5';
  return 'text-rose-400/70 bg-rose-500/5';
}

function RetentionCurve({ cohorts }: { cohorts: Cohort[] }) {
  if (cohorts.length === 0) {
    return (
      <p className="text-sm text-gray-500 px-5 py-4">
        No signup cohorts in this range yet.
      </p>
    );
  }
  const cell = 'px-4 py-2 text-sm tabular-nums text-right';
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[440px]">
        <thead>
          <tr className="border-b border-white/5">
            <th className="px-4 py-2 text-left text-[11px] text-gray-500 font-medium">
              Cohort week
            </th>
            <th className="px-4 py-2 text-right text-[11px] text-gray-500 font-medium">
              Signups
            </th>
            <th className="px-4 py-2 text-right text-[11px] text-gray-500 font-medium">
              Came back by day 7
            </th>
            <th className="px-4 py-2 text-right text-[11px] text-gray-500 font-medium">
              by day 30
            </th>
          </tr>
        </thead>
        <tbody>
          {cohorts.map((c) => (
            <tr key={c.cohort_start} className="border-b border-white/5 last:border-0">
              <td className="px-4 py-2 text-sm text-gray-300">{c.cohort_start}</td>
              <td className={`${cell} text-gray-400`}>{c.size.toLocaleString()}</td>
              <td className={`${cell} ${heat(c.d7_pct)}`}>{fmtPct(c.d7_pct)}</td>
              <td className={`${cell} ${heat(c.d30_pct)}`}>{fmtPct(c.d30_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-4 py-2.5 text-[11px] text-gray-600">
        A dash means the cohort has not reached that age yet, not that nobody came back.
      </p>
    </div>
  );
}

/**
 * The founder scorecard: seven outcome numbers, grouped by the question they
 * answer, each against the previous period of the same length.
 *
 * A visit means a live check-in promoted to VERIFIED, so active users is a real
 * denominator for visits rather than a larger, unrelated population.
 */
export function AnalyticsPage(): React.ReactElement {
  const [[from, to], setRange] = useState<[string | null, string | null]>(defaultRange);
  // The scorecard only understands the presets, so the control hides its custom
  // inputs and a range is always one of them.
  const { data, loading, error } = useFounderScorecard(rangePreset(from, to) ?? 30);
  const h: Scorecard['headlines'] | undefined = data?.headlines;
  const allTime = data?.window.all_time ?? false;
  const ns = h?.verified_visits;

  return (
    <div className="min-h-screen bg-[#0b0b14]">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-5">
        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-white tracking-tight">Analytics</h1>
            <p className="mt-1 text-xs text-gray-500">
              Is the business working? A visit means a check-in we verified on site.
            </p>
          </div>
          <DateRangeControl from={from} to={to} onChange={(f, t) => setRange([f, t])} custom={false} />
        </header>

        {/* Always reachable — this page works on its own and must not be
            hidden behind any single metric group's success (see the
            `h.social_graph` gate around the friend-analytics link below). */}
        <div>
          <Link
            to="/analytics/referrals"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-300 hover:text-violet-200 transition-colors"
          >
            View referral analytics
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {error && <ErrorBanner message={error} />}
        {loading && (
          <div className="flex justify-center py-24">
            <Spinner />
          </div>
        )}

        {!loading && data && h && (
          <>
            {/* North star. The one place this page raises its voice. */}
            <section className="relative overflow-hidden rounded-2xl border border-violet-500/20 bg-gradient-to-b from-[#1b1730] to-[#14141f] px-6 pt-6 pb-14">
              {ns?.series && <NorthStarChart series={ns.series} />}
              <div className="relative">
                <p className="text-xs text-violet-300/70 font-medium">
                  Verified visits
                  <InfoTip metric="verified_visits" />
                </p>
                <div className="mt-1 flex items-baseline gap-4 flex-wrap">
                  <span className="text-6xl font-bold text-white tabular-nums tracking-tighter">
                    {fmt(ns)}
                  </span>
                  <Delta m={ns} />
                  <Baseline m={ns} allTime={allTime} />
                </div>
                <p className="mt-2 text-xs text-gray-500 max-w-md">
                  People who actually turned up at a venue. Everything else on this page
                  exists to explain this number.
                </p>
              </div>
            </section>

            <Group
              question="Are people showing up?"
              caption="Verified visitors, and how many distinct people that is."
            >
              {h.active_users ? (
                <>
                  <Stat
                    label="Active users"
                    metric={h.active_users}
                    hint="visited in this window"
                    info="active_users"
                    allTime={allTime}
                  />
                  <div className="flex-1 min-w-[150px] px-5 py-4">
                    <p className="text-[11px] text-gray-500 font-medium">
                      Weekly / monthly active
                      <InfoTip metric="wau_mau" />
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-white tabular-nums tracking-tight">
                      {h.active_users.wau.toLocaleString()}
                      <span className="text-gray-600 font-normal"> / </span>
                      {h.active_users.mau.toLocaleString()}
                    </p>
                    <p className="mt-1.5 text-[11px] text-gray-600">
                      always trailing 7 and 30 days, whatever the range
                    </p>
                  </div>
                </>
              ) : (
                <Unavailable label="Active users" />
              )}
              {h.signups ? (
                <Stat
                  label="New signups"
                  metric={h.signups.total}
                  hint="new accounts created"
                  info="new_signups"
                  allTime={allTime}
                />
              ) : (
                <Unavailable label="New signups" />
              )}
            </Group>

            <Group
              question="Do they stay?"
              caption="The biggest hole. Activation lags a week so every cohort gets the same clock."
            >
              {h.activation ? (
                <Stat
                  label="Reached a first visit"
                  info="activation"
                  metric={h.activation}
                  hint={
                    h.activation.maturing > 0
                      ? `${h.activation.maturing.toLocaleString()} newer signups still have time`
                      : undefined
                  }
                  allTime={allTime}
                />
              ) : (
                <Unavailable label="Activation" />
              )}
              {h.retention ? (
                <>
                  <Stat label="Back by day 7" info="retention_d7" metric={h.retention.d7} allTime={allTime} />
                  <Stat label="Back by day 30" info="retention_d30" metric={h.retention.d30} allTime={allTime} />
                  <Stat
                    label="Visited more than once"
                    info="repeat_visitors"
                    metric={h.retention.repeat_pct}
                    allTime={allTime}
                  />
                </>
              ) : (
                <Unavailable label="Retention" />
              )}
            </Group>

            <Group
              question="Is the supply side alive?"
              caption="Venues running deals, and whether the social graph is filling in."
            >
              {h.deal_venues ? (
                <>
                  <Stat
                    label="Venues running deals"
                    info="deal_venues"
                    metric={h.deal_venues.venues}
                    allTime={allTime}
                  />
                  <Stat
                    label="Visits at those venues"
                    metric={h.deal_venues.attributed_visits}
                    hint="what we drove them"
                    info="deal_venue_visits"
                    allTime={allTime}
                  />
                </>
              ) : (
                <Unavailable label="Deal venues" />
              )}
              {h.social_graph ? (
                <>
                  <Stat
                    label="Friends per user"
                    info="friends_per_user"
                    metric={h.social_graph.avg_friends}
                    allTime={allTime}
                  />
                  <Stat
                    label="Has at least one friend"
                    info="pct_with_friend"
                    metric={h.social_graph.pct_with_friend}
                    allTime={allTime}
                  />
                  <div className="flex-1 min-w-[150px] px-5 py-4 flex items-center">
                    <Link
                      to="/analytics/friends"
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-300 hover:text-violet-200 transition-colors"
                    >
                      View friend analytics
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </>
              ) : (
                <Unavailable label="Social graph" />
              )}
            </Group>

            <div className="grid lg:grid-cols-2 gap-5">
              <section className="rounded-2xl border border-white/5 bg-[#14141f] overflow-hidden">
                <div className="px-5 py-4 border-b border-white/5">
                  <h2 className="text-sm font-semibold text-gray-200">Retention by cohort</h2>
                  <p className="mt-1 text-[11px] text-gray-500">
                    Watch for the tail flattening out instead of reaching zero.
                  </p>
                </div>
                {h.retention ? (
                  <RetentionCurve cohorts={h.retention.cohorts} />
                ) : (
                  <p className="text-sm text-gray-500 px-5 py-4">Could not be computed.</p>
                )}
              </section>

              <section className="rounded-2xl border border-white/5 bg-[#14141f] overflow-hidden">
                <div className="px-5 py-4 border-b border-white/5">
                  <h2 className="text-sm font-semibold text-gray-200">Where signups come from</h2>
                  <p className="mt-1 text-[11px] text-gray-500">
                    {h.signups?.note ??
                      'Referral channels are tracked; nothing else is recorded at signup.'}
                  </p>
                </div>
                {h.signups && h.signups.by_channel.length > 0 ? (
                  <div className="divide-y divide-white/5">
                    {h.signups.by_channel.map((c) => (
                      <div
                        key={`${c.channel}:${c.entered_via ?? ''}`}
                        className="flex items-center justify-between px-5 py-3"
                      >
                        <div>
                          <p className="text-sm text-gray-200">{c.channel}</p>
                          {c.entered_via && (
                            <p className="text-[11px] text-gray-600">
                              entered at {c.entered_via}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-white tabular-nums">
                            {c.count.toLocaleString()}
                          </p>
                          {c.prior !== null && (
                            <p className="text-[11px] text-gray-600 tabular-nums">
                              from {c.prior.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 px-5 py-4">
                    Nobody signed up in this window.
                  </p>
                )}
              </section>
            </div>

            <p className="text-[11px] text-gray-600 leading-relaxed max-w-3xl">
              {allTime
                ? 'Every record we have. Nothing precedes it, so no comparison is shown.'
                : `Compared against ${data.window.prior_from.slice(0, 10)} to ${data.window.prior_to.slice(0, 10)}.`}{' '}
              Rates count live accounts only, so a deleted user is never counted as one who
              failed to activate. Updated {new Date(data.generated_at).toLocaleString()}.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default AnalyticsPage;
